const en = {
  _meta: {
    code: "en",
    name: "English",
    reportLanguage: "English",
  },

  nav: {
    home:       "Home",
    services:   "Services",
    pricing:    "Pricing",
    about:      "About",
    contact:    "Contact",
    login:      "Sign In",
    dashboard:  "Dashboard",
    screening:  "Analysis",
    reports:    "My Reports",
    account:    "My Account",
    admin:      "Admin",
    logout:     "Sign Out",
  },

  auth: {
    title:          "Sign In",
    subtitle:       "Enter your email to receive a sign-in link.",
    emailLabel:     "Email address",
    emailPh:        "you@example.com",
    btnSend:        "Send sign-in link",
    btnSending:     "Sending…",
    successTitle:   "Email sent!",
    successDesc:    "Check your inbox and click the sign-in link. It expires in 15 minutes.",
    errorInvalid:   "Invalid email.",
    errorServer:    "Error sending email. Please try again.",
    orContinueWith: "or continue with",
    google:         "Continue with Google",
    noAccount:      "First time here? Your account is created automatically.",
    linkExpired:    "The link has expired or was already used. Request a new one.",
  },

  dashboard: {
    title:          "Dashboard",
    welcome:        (name) => `Welcome, ${name || "user"}!`,
    kpiReports:     "Reports generated",
    kpiRemaining:   "Reports remaining",
    kpiPlan:        "Your plan",
    kpiExpires:     "Expires",
    noPlan:         "No active subscription",
    btnNewReport:   "New report",
    btnViewAll:     "View all reports",
    recentTitle:    "Recent reports",
    noReports:      "You haven't generated any reports yet.",
    noReportsDesc:  "Start with a free analysis of your manuscript.",
    btnStart:       "Start analysis",
  },

  screening: {
    title:          "Manuscript Analysis",
    sub:            "Universal Core · Genre Modules · Romanian Market Layer",
    versionBadge:   "beta",
    labelTitleReq:  "Manuscript title *",
    labelAuthor:    "Author (optional)",
    labelGenre:     "Literary genre *",
    labelTier:      "Report type *",
    labelTitlePh:   "e.g. Shadows of Cluj",
    labelAuthorPh:  "Author name",
    labelGenrePh:   "Select genre",
    labelTierPh:    "Select report type",
    labelText:      "Manuscript text *",
    labelTextHint:  "Accepted files: .txt, .docx, .pdf",
    labelDropzone:  "Drag file (.txt, .docx, .pdf) or click to select",
    labelPastePh:   "or paste text directly…",
    labelWordCount: (n) => `${n} words`,
    labelWordMin:   "Minimum 500 words recommended",
    genreActivated: (label) => `${label} activated`,
    genreEngines:   (n, names) => `${n} engines: ${names}`,
    btnSubmit:      "Generate Report",
    btnSubmitting:  "Analyzing…",
    btnNew:         "← New analysis",
    btnExportPDF:   "⬇ Export PDF",
    btnExportDOCX:  "⬇ Export DOCX",
    btnExporting:   "Generating…",
    btnLibLoading:  "Loading…",
    errRequired:    "Please fill in all required fields.",
    errAuth:        "You must be signed in for this report type.",
    errNoSub:       "You don't have an active subscription for this report type.",
    errAPI:         "Error generating report. Please try again.",
    errPDF:         "Error generating PDF.",
    errDOCX:        "Error generating DOCX.",
    loadingMsgs: [
      "Analyzing Universal Core…",
      "Evaluating genre module…",
      "Calculating Romanian Market Layer…",
      "Evaluating market potential…",
      "Finalizing editorial verdict…",
    ],
    freemiumBadge:  "Free analysis",
    freemiumDesc:   "You get the Hook score and genre detection. For a full report, choose a package.",
    freemiumLimit:  "You have reached the limit of 3 free analyses per day.",
    reportTitle:    "FEUR Report",
    reportVersion:  "v1.0",
    reportUnknown:  "Unknown",
    savedOk:        "Report saved to your account.",
    tabUC:          "Universal Core",
    tabMarket:      "Romanian Market",
    sectionStrengths:  "✓ Strengths",
    sectionWeaknesses: "⚠ Issues detected",
    sectionRecs:       "Editorial recommendations",
    sectionGrammar:    "Grammar & diacritics",
    sectionGenreFit:   (genre) => `Genre fit · ${genre}`,
    sectionSegment:    "Primary reader",
    sectionChannels:   "Recommended channels",
    sectionLogline:    "Logline",
  },

  reports: {
    title:          "My Reports",
    searchPh:       "Search by title or author…",
    filterGenre:    "All genres",
    filterVerdict:  "All verdicts",
    sortDate:       "Date (recent)",
    sortScore:      "FEUR Score ↓",
    sortTitle:      "Title A–Z",
    count:          (f, t) => `${f} / ${t} reports`,
    empty:          "You have no saved reports.",
    emptyFiltered:  "No reports match the filters.",
    btnDownloadPDF: "⬇ PDF",
    btnDownloadDOCX:"⬇ DOCX",
    btnDelete:      "Delete",
    confirmDelete:  "Delete this report?",
    labelConf:      "Confidence",
  },

  pricing: {
    title:          "Pricing",
    subtitle:       "Choose the right plan for you",
    perReport:      "per report",
    monthly:        "monthly",
    yearly:         "yearly",
    saveYearly:     "Save 20%",
    btnBuy:         "Buy now",
    btnSubscribe:   "Subscribe",
    btnCurrent:     "Your current plan",
    freemiumTitle:  "Free",
    freemiumDesc:   "3 free analyses per day · Hook score + genre detection",
    popular:        "Most popular",
    tabPerReport:   "Per report",
    tabMonthly:     "Monthly",
    tabYearly:      "Yearly",
  },

  account: {
    title:          "My Account",
    labelName:      "Name",
    labelEmail:     "Email",
    labelLang:      "Language",
    labelPlan:      "Active plan",
    labelExpires:   "Expires on",
    labelReports:   "Reports used",
    btnSave:        "Save",
    btnSaved:       "Saved!",
    historyTitle:   "Payment history",
    noPayments:     "No payments recorded.",
  },

  admin: {
    title:          "Admin Dashboard",
    statUsers:      "Total users",
    statReports:    "Reports generated",
    statRevenue:    "Total revenue",
    statActiveSubs: "Active subscriptions",
    statToday:      "Reports today",
    statMonth:      "Revenue this month",
    navStats:       "Statistics",
    navUsers:       "Users",
    navReports:     "Reports",
    navRevenue:     "Revenue",
    searchUsers:    "Search user…",
    makeAdmin:      "Make admin",
    removeAdmin:    "Revoke admin",
  },

  common: {
    loading:        "Loading…",
    error:          "An error occurred.",
    retry:          "Try again",
    save:           "Save",
    cancel:         "Cancel",
    delete:         "Delete",
    close:          "Close",
    back:           "Back",
    next:           "Next",
    yes:            "Yes",
    no:             "No",
    unknown:        "Unknown",
    currency:       "RON",
    footerNote:     "FEUR-1 Architecture Specification v1.0 · Narratiq · Powered by EditorialAI România",
  },

  verdicts: {
    publish_now:    "Publish Now",
    minor_revision: "Minor Revision",
    major_revision: "Major Revision",
    structural_edit:"Structural Edit",
    rewrite:        "Rewrite",
    reject:         "Reject",
  },

  rmlVerdicts: [
    { min: 80, label: "Excellent fit for the Romanian market"                         },
    { min: 65, label: "Good fit for the Romanian market, with careful positioning"    },
    { min: 50, label: "Best suited for indie / assisted self-publishing"              },
    { min: 35, label: "Moderate Romanian potential, local niche"                      },
    { min: 0,  label: "Limited Romanian market potential in current form"             },
  ],
};

export default en;